import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";
import api from "@/lib/api";

// Address input with live autocomplete suggestions from the geocoder.
// Drop-in replacement for a plain Input: value/onChange work on the raw string.
export const AddressInput = ({ value, onChange, onPick, className = "", icon = false, ...rest }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [focused, setFocused] = useState(false);
  const skipNextFetch = useRef(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!focused) return; // only suggest while the user is typing in this input
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    const q = (value || "").trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const t = setTimeout(() => {
      api
        .get("/geocode/suggest", { params: { q } })
        .then(({ data }) => {
          setSuggestions(data);
          setOpen(data.length > 0);
          setHighlight(-1);
        })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [value, focused]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const pick = (s) => {
    skipNextFetch.current = true;
    onChange(s.label);
    if (onPick) onPick(s);
    setOpen(false);
    setSuggestions([]);
  };

  const onKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative flex-1" ref={wrapRef}>
      {icon && <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#1C1917] z-10" />}
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => {
          setFocused(true);
          if (suggestions.length) setOpen(true);
        }}
        onBlur={() => setFocused(false)}
        autoComplete="off"
        className={className}
        {...rest}
      />
      {open && (
        <div
          className="absolute z-[1300] left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg overflow-hidden"
          data-testid="address-suggestions"
        >
          {suggestions.map((s, i) => (
            <button
              key={s.label}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 ${
                i === highlight ? "bg-[#FFF7ED] text-[#EA580C]" : "text-[#1C1917] hover:bg-stone-50"
              }`}
              data-testid={`address-suggestion-${i}`}
            >
              <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#F97316]" />
              <span>{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
